import React, { useEffect, useState, useCallback } from "react";
import { Truck, CheckCircle2, Clock, AlertTriangle, Send, Loader2, ExternalLink } from "lucide-react";
import api from "@/lib/api";

const STATUS_META = {
  queued:    { label: "Queued",              icon: Clock,          tone: "amber",   dot: "bg-amber-400" },
  sent:      { label: "Sent to partner",     icon: Send,           tone: "sky",     dot: "bg-sky-400" },
  confirmed: { label: "Partner confirmed",   icon: CheckCircle2,   tone: "violet",  dot: "bg-violet-400" },
  filed:     { label: "Filed with state",    icon: CheckCircle2,   tone: "emerald", dot: "bg-emerald-400" },
  rejected:  { label: "Needs your attention",icon: AlertTriangle,  tone: "rose",    dot: "bg-rose-400" },
};

export default function Submissions() {
  const [items, setItems] = useState(null);
  const [partnerReady, setPartnerReady] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/submissions");
      setItems(data.items || []);
      setPartnerReady(!!data.partner_configured);
    } catch { setItems([]); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000); // poll while user is on the view
    return () => clearInterval(t);
  }, [load]);

  if (items === null) return <div className="h-40 rounded-2xl bg-white/5 animate-pulse" />;

  return (
    <div className="space-y-6" data-testid="submissions-view">
      <div>
        <h2 className="font-display font-black text-2xl mb-1">Filings in flight</h2>
        <p className="text-white/55 text-sm max-w-2xl">
          Once you approve a renewal package, Dox routes it to your filing partner and reports back as the status changes — queued, sent, confirmed, filed.
          {!partnerReady && <span className="text-white/40"> A filing partner isn&apos;t wired to this workspace yet — submissions will stay queued until one is configured.</span>}
        </p>
      </div>

      {items.length === 0 ? (
        <div className="glass-card p-10 text-center">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-white/5 border border-white/10 mb-4">
            <Truck size={22} className="text-white/50" />
          </div>
          <div className="text-white/80 font-semibold">Nothing to send yet.</div>
          <div className="text-white/45 text-sm mt-1">Approve a renewal package and Dox will kick off the filing.</div>
        </div>
      ) : (
        <div className="grid gap-4">
          {items.map((s) => {
            const meta = STATUS_META[s.status] || STATUS_META.queued;
            const Icon = meta.icon;
            return (
              <div key={s.id} className="glass-card p-5" data-testid={`submission-${s.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="h-11 w-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                      <Icon size={18} className="text-white/80" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-white truncate">{s.package_title || "Renewal package"}</div>
                      <div className="text-xs text-white/50 mt-0.5">
                        {s.filing?.jurisdiction && <>Filing to <span className="text-white/70">{s.filing.jurisdiction}</span> · </>}
                        {s.business?.name}
                      </div>
                    </div>
                  </div>
                  <div className={`inline-flex items-center gap-2 rounded-full glass-strong border border-white/10 px-3 py-1.5 text-xs font-semibold`}>
                    <span className={`h-2 w-2 rounded-full ${meta.dot} ${(s.status !== 'filed' && s.status !== 'rejected') ? 'animate-pulse' : ''}`} />
                    {meta.label}
                  </div>
                </div>

                <div className="mt-4 border-t border-white/5 pt-4">
                  <div className="text-[10px] tracking-widest uppercase text-white/40 mb-2">Timeline</div>
                  <ol className="space-y-1.5">
                    {(s.history || []).slice().reverse().map((h, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-white/60">
                        <span className={`mt-1.5 h-1.5 w-1.5 rounded-full ${(STATUS_META[h.status] || meta).dot} shrink-0`} />
                        <span className="min-w-0">
                          <span className="text-white/80">{h.message || (STATUS_META[h.status]?.label || h.status)}</span>
                          {h.at && <span className="text-white/40 ml-2">{new Date(h.at).toLocaleString()}</span>}
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>

                {s.external_reference && (
                  <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-white/55">
                    <ExternalLink size={12} /> Ref #{s.external_reference}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Small button used inside the document dialog to hand off an existing package to filing.
 */
export function SubmitForMeButton({ packageId, onSent }) {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/renewal-packages/${packageId}/submit`, {});
      setSent(true);
      onSent && onSent(data);
    } finally { setBusy(false); }
  };
  return (
    <button
      data-testid={`submit-for-me-${packageId}`}
      onClick={submit}
      disabled={busy || sent}
      className="inline-flex items-center gap-2 rounded-full bg-dox-magenta px-4 py-2.5 text-sm font-bold text-white hover:opacity-90 transition-opacity disabled:opacity-60"
    >
      {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
      {sent ? "Handed off to Dox" : (busy ? "Sending…" : "Submit for me")}
    </button>
  );
}
